import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  UserPlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../slices/types";
import api from "../../helpers/api_helper";
import { getAppIdFromAccessToken } from "../../helpers/jwt";
import {
  createRole,
  createUser,
  fetchRoles,
  clearCreateRoleError,
  clearLastCreatedRole,
  clearCreateUserError,
  clearLastCreatedUser,
  type CreateRolePayload,
  type CreateUserPayload,
} from "../../slices/users/userManagementSlice";
import { fetchRolesWithUsers } from "../../slices/roles/rolesSlice";

interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
}

interface App {
  id: string;
  app_name: string;
  version: string;
  created_at: string;
  updated_at: string;
}

// Roles allowed in the create-user dropdown.
// IDs are loaded from the backend instead of being hard-coded.
const allowedRoleNames = new Set([
  "finance",
  "agent",
  "agent (internal)",
  "agent (external)",
  "sub admin",
  "sub_admin",
  "roll caller",
  "roll_caller",
]);

const RoleCreation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    roles: availableRoles,
    loading,
    error,
    createRoleLoading,
    createRoleError,
    lastCreatedRole,
    createUserLoading,
    createUserError,
    lastCreatedUser,
  } = useAppSelector((state) => state.UserManagement);
  const { rolesWithUsers, fetchWithUsersLoading, fetchWithUsersError } =
    useAppSelector((state) => state.Roles as any);

  const [roles, setRoles] = useState<Role[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [showCreateRoleForm, setShowCreateRoleForm] = useState(false);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [showEditRoleForm, setShowEditRoleForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [deletingRole, setDeletingRole] = useState<any>(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [deleteUserError, setDeleteUserError] = useState<string | null>(null);
  const [deleteUserSuccess, setDeleteUserSuccess] = useState<string | null>(
    null,
  );

  const [newRole, setNewRole] = useState({
    name: "",
  });

  const [newUser, setNewUser] = useState({
    mobileNumber: "",
    password: "",
    firstName: "",
    lastName: "",
    selectedRoleId: null as number | null,
    commissionPercentage: "",
  });

  const selectableRoles = useMemo(
    () =>
      (availableRoles || []).filter((role: any) =>
        allowedRoleNames.has(
          String(role.name || "")
            .toLowerCase()
            .trim(),
        ),
      ),
    [availableRoles],
  );

  // Resolve role names using the actual roles returned by the backend.
  const getRoleNameFromId = (roleId: number | null): string => {
    if (!roleId) return "";
    const role = selectableRoles.find(
      (r: any) => Number(r.id) === Number(roleId),
    );
    return role ? role.name : "";
  };

  const isAgentRoleId = (roleId: number | null): boolean =>
    getRoleNameFromId(roleId).toLowerCase().includes("agent");

  // Helper function to format role name for display (remove "(internal)" from "Agent (internal)")
  const formatRoleNameForDisplay = (roleName: string): string => {
    if (!roleName) return "";

    const normalized = roleName.toLowerCase().trim();

    if (normalized === "roll caller" || normalized === "roll_caller") {
      return "Caller";
    }

    // Replace "Agent (internal)" with "Agent"
    return roleName.replace(/\s*\(internal\)/gi, "");
  };

  const [roleErrors, setRoleErrors] = useState({
    name: "",
  });

  const [userErrors, setUserErrors] = useState({
    mobileNumber: "",
    password: "",
    firstName: "",
    lastName: "",
    roleSelection: "",
    commissionPercentage: "",
  });

  // Newly created users may be kept temporarily in component state
  // while the server-backed roles/users data refreshes.
  //
  // Do NOT restore this list from localStorage. The production API/database
  // is the source of truth; persisted browser records can become stale after
  // users are deleted or changed on the server.
  const [createdUsers, setCreatedUsers] = useState<any[]>([]);

  // Check if role name already exists
  const checkRoleNameExists = (name: string) => {
    return availableRoles.some(
      (role) => role.name.toLowerCase() === name.toLowerCase(),
    );
  };

  // Check if mobile number already exists
  const checkMobileNumberExists = (mobileNumber: string) => {
    return createdUsers.some((user) => user.mobileNumber === mobileNumber);
  };

  // Check if role form has any validation errors
  const hasRoleValidationErrors = () => {
    const nameError = validateRoleName(newRole.name);

    // Check for duplicate role name
    let duplicateError = "";
    if (!nameError && newRole.name) {
      if (checkRoleNameExists(newRole.name)) {
        duplicateError = "Role name already exists";
      }
    }

    return !!(nameError || duplicateError);
  };

  // Check if user form has any validation errors
  const hasUserValidationErrors = () => {
    const mobileError = validateMobileNumber(newUser.mobileNumber);
    const passwordError = validatePassword(newUser.password);
    const firstNameError = validateName(newUser.firstName, "First name");
    const lastNameError = validateName(newUser.lastName, "Last name");
    const roleSelectionError = !newUser.selectedRoleId
      ? "Please select a role"
      : "";
    const isAgentRole = isAgentRoleId(newUser.selectedRoleId);
    const commissionError = isAgentRole
      ? validateCommission(String(newUser.commissionPercentage))
      : "";

    // Check for duplicate mobile number
    let duplicateError = "";
    if (!mobileError && newUser.mobileNumber.length === 10) {
      if (checkMobileNumberExists(newUser.mobileNumber)) {
        duplicateError = "Phone number already exists";
      }
    }

    return !!(
      mobileError ||
      passwordError ||
      firstNameError ||
      lastNameError ||
      roleSelectionError ||
      duplicateError ||
      commissionError
    );
  };

  // Fetch roles and apps from API
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchRolesWithUsers());
    fetchApps();
  }, [dispatch]);

  const fetchApps = async () => {
    try {
      const response = await api.get("/apps/");
      if (response.data && Array.isArray(response.data)) {
        setApps(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching apps:", error);
    }
  };

  // Update local roles when API data changes
  useEffect(() => {
    if (availableRoles && availableRoles.length > 0) {
      const mappedRoles: Role[] = availableRoles.map((role: any) => ({
        id: role.id,
        name: role.name,
        description:
          role.description || `${formatRoleNameForDisplay(role.name)} role`,
        permissions: [role.name],
        isActive: true,
        createdAt: new Date().toISOString().slice(0, 19).replace("T", " "),
      }));
      setRoles(mappedRoles);
    }
  }, [availableRoles]);

  // Refresh roles list when role is created
  useEffect(() => {
    if (lastCreatedRole && lastCreatedRole.id) {
      // Refresh the roles list from API
      dispatch(fetchRoles());
    }
  }, [lastCreatedRole, dispatch]);

  // Add created user to table when user is successfully created
  useEffect(() => {
    if (lastCreatedUser && lastCreatedUser.user) {
      const newUserData = {
        id: Number((lastCreatedUser.user as any).id),
        name: `${lastCreatedUser.user.first_name} ${lastCreatedUser.user.last_name}`,
        mobileNumber: lastCreatedUser.user.mobile_number,
        points: lastCreatedUser.user.points || 1000,
        roleId: lastCreatedUser.role.id,
        role: lastCreatedUser.role.name,
        isActive: true,
        createdAt: new Date().toISOString().slice(0, 19).replace("T", " "),
      };
      setCreatedUsers((prev) => {
        const updated = [newUserData, ...prev];
        // Save to localStorage
        localStorage.setItem("createdUsers", JSON.stringify(updated));
        return updated;
      });
    }
  }, [lastCreatedUser]);

  // Clear success messages after 3 seconds
  useEffect(() => {
    if (lastCreatedRole) {
      const timer = setTimeout(() => {
        dispatch(clearLastCreatedRole());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastCreatedRole, dispatch]);

  useEffect(() => {
    if (lastCreatedUser) {
      const timer = setTimeout(() => {
        dispatch(clearLastCreatedUser());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastCreatedUser, dispatch]);

  // Clear delete success message after 3 seconds
  useEffect(() => {
    if (deleteUserSuccess) {
      const timer = setTimeout(() => {
        setDeleteUserSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [deleteUserSuccess]);

  // Display backend error in form when createUserError occurs (only for mobile number errors)
  useEffect(() => {
    if (createUserError) {
      const errorMessage = createUserError;
      // Only set error in mobile number field if error is related to mobile number
      if (
        errorMessage.toLowerCase().includes("mobile number") ||
        errorMessage.toLowerCase().includes("mobile") ||
        errorMessage.toLowerCase().includes("phone")
      ) {
        setUserErrors((prev) => ({
          ...prev,
          mobileNumber: errorMessage,
        }));
      }
      // Don't set errors in other fields - just let Redux handle the error display
    }
  }, [createUserError]);

  const handleCreateRole = () => {
    setShowCreateRoleForm(true);
  };

  const handleCreateUser = () => {
    setShowCreateUserForm(true);
  };

  const handleCloseRoleForm = () => {
    setShowCreateRoleForm(false);
    setShowEditRoleForm(false);
    setEditingRole(null);
    setNewRole({
      name: "",
    });
    setRoleErrors({
      name: "",
    });
  };

  const handleCloseUserForm = () => {
    setShowCreateUserForm(false);
    setShowEditUserForm(false);
    setEditingUser(null);
    setNewUser({
      mobileNumber: "",
      password: "",
      firstName: "",
      lastName: "",
      selectedRoleId: null,
      commissionPercentage: "",
    });
    setUserErrors({
      mobileNumber: "",
      password: "",
      firstName: "",
      lastName: "",
      roleSelection: "",
      commissionPercentage: "",
    });
    // Clear Redux error when form is closed
    if (createUserError) {
      dispatch(clearCreateUserError());
    }
  };

  // Validation functions
  const validateRoleName = (value: string) => {
    if (!value) return "Role name is required";
    if (value.length < 3) return "Role name must be at least 3 characters";
    if (value.length > 50) return "Role name must be maximum 50 characters";
    if (!/^[a-zA-Z]/.test(value))
      return "Role name must start with an alphabetic character";
    if (!/^[a-zA-Z0-9_\s-]+$/.test(value))
      return "Role name can only contain alphanumeric characters, spaces, underscores, and hyphens";
    return "";
  };

  const validateMobileNumber = (value: string) => {
    if (!value) return "Mobile number is required";
    if (!/^\d+$/.test(value)) return "Mobile number must contain only digits";
    if (value.length !== 10) return "Mobile number must be exactly 10 digits";
    return "";
  };

  const validatePassword = (value: string) => {
    if (!value) return "Password is required";
    if (value.length > 12) return "Password must be maximum 12 characters";
    return "";
  };

  const validateCommission = (value: string) => {
    if (value === "") return "Commission is required";
    const n = Number(value);
    if (!Number.isFinite(n)) return "Commission must be a number";
    if (n < 0 || n > 100) return "Commission must be between 0 and 100";
    return "";
  };

  const validateName = (value: string, fieldName: string) => {
    if (!value) return `${fieldName} is required`;
    if (value.length > 30) return `${fieldName} must be maximum 30 characters`;
    if (!/^[a-zA-Z]/.test(value))
      return `${fieldName} must start with an alphabetic character`;
    if (!/^[a-zA-Z0-9_-]+$/.test(value))
      return `${fieldName} can only contain alphanumeric characters, underscores, and minus`;
    if (/_{2,}|-{2,}/.test(value))
      return `${fieldName} cannot have multiple consecutive underscores or minus characters`;
    if (value.endsWith("_") || value.endsWith("-"))
      return `${fieldName} cannot end with underscore or minus`;
    return "";
  };

  const handleRoleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "name") {
      const limitedValue = value.slice(0, 50);
      setNewRole((prev) => ({ ...prev, [name]: limitedValue }));

      const formatError = validateRoleName(limitedValue);
      let duplicateError = "";

      if (!formatError && limitedValue) {
        if (checkRoleNameExists(limitedValue)) {
          duplicateError = "Role name already exists";
        }
      }

      setRoleErrors((prev) => ({
        ...prev,
        [name]: formatError || duplicateError,
      }));
    }
  };

  const handleUserInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Handle mobile number
    if (name === "mobileNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setNewUser((prev) => ({ ...prev, [name]: numericValue }));

      // Clear Redux error when user starts typing
      if (createUserError) {
        dispatch(clearCreateUserError());
      }

      const formatError = validateMobileNumber(numericValue);
      let duplicateError = "";

      if (!formatError && numericValue.length === 10) {
        if (checkMobileNumberExists(numericValue)) {
          duplicateError = "Phone number already exists";
        }
      }

      setUserErrors((prev) => ({
        ...prev,
        [name]: formatError || duplicateError,
      }));
      return;
    }

    // Handle password
    if (name === "password") {
      const limitedValue = value.slice(0, 12);
      setNewUser((prev) => ({ ...prev, [name]: limitedValue }));
      setUserErrors((prev) => ({
        ...prev,
        [name]: validatePassword(limitedValue),
      }));
      return;
    }

    // Handle first name and last name
    if (name === "firstName" || name === "lastName") {
      const fieldName = name === "firstName" ? "First name" : "Last name";
      const limitedValue = value.slice(0, 30);
      setNewUser((prev) => ({ ...prev, [name]: limitedValue }));
      setUserErrors((prev) => ({
        ...prev,
        [name]: validateName(limitedValue, fieldName),
      }));
      return;
    }

    // Handle role selection
    if (name === "selectedRoleId") {
      const roleId = value ? parseInt(value) : null;
      setNewUser((prev) => ({ ...prev, [name]: roleId }));
      setUserErrors((prev) => ({
        ...prev,
        roleSelection: "",
        commissionPercentage: "",
      }));
      if (!isAgentRoleId(roleId)) {
        setNewUser((prev) => ({ ...prev, commissionPercentage: "" }));
      }
      return;
    }

    // Default handling
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitRole = async () => {
    const nameError = validateRoleName(newRole.name);
    let duplicateError = "";

    if (!nameError && newRole.name) {
      if (checkRoleNameExists(newRole.name)) {
        duplicateError = "Role name already exists";
      }
    }

    setRoleErrors({
      name: nameError || duplicateError,
    });

    if (nameError || duplicateError) {
      return;
    }

    const rolePayload: CreateRolePayload = {
      name: newRole.name,
    };

    try {
      await dispatch(createRole(rolePayload)).unwrap();
      handleCloseRoleForm();
    } catch (error) {
      console.error("Failed to create role:", error);
    }
  };

  const handleSubmitUser = async () => {
    const mobileError = validateMobileNumber(newUser.mobileNumber);
    const passwordError = validatePassword(newUser.password);
    const firstNameError = validateName(newUser.firstName, "First name");
    const lastNameError = validateName(newUser.lastName, "Last name");
    const roleSelectionError = !newUser.selectedRoleId
      ? "Please select a role"
      : "";
    const isAgentRole = isAgentRoleId(newUser.selectedRoleId);
    const commissionError = isAgentRole
      ? validateCommission(String(newUser.commissionPercentage))
      : "";

    let duplicateError = "";
    if (!mobileError && newUser.mobileNumber.length === 10) {
      if (checkMobileNumberExists(newUser.mobileNumber)) {
        duplicateError = "Phone number already exists";
      }
    }

    // Get app_id from access token
    const appId = getAppIdFromAccessToken();
    if (!appId) {
      setUserErrors({
        mobileNumber: mobileError || duplicateError,
        password: passwordError,
        firstName: firstNameError,
        lastName: lastNameError,
        roleSelection: roleSelectionError || "App ID not found in token",
        commissionPercentage: commissionError,
      });
      return;
    }

    setUserErrors({
      mobileNumber: mobileError || duplicateError,
      password: passwordError,
      firstName: firstNameError,
      lastName: lastNameError,
      roleSelection: roleSelectionError,
      commissionPercentage: commissionError,
    });

    if (
      mobileError ||
      passwordError ||
      firstNameError ||
      lastNameError ||
      roleSelectionError ||
      duplicateError ||
      commissionError
    ) {
      return;
    }

    const userPayload: CreateUserPayload = {
      operation: "create",
      mobile_number: newUser.mobileNumber,
      password: newUser.password,
      first_name: newUser.firstName,
      last_name: newUser.lastName,
      role_id: newUser.selectedRoleId!,
      app_id: appId,
      is_staff: true,
      is_active: true,
    };

    if (isAgentRole) {
      (userPayload as any).commission_percentage = Number(
        newUser.commissionPercentage,
      );
    }

    try {
      if (isAgentRole) {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const yyyy = (d: Date) => d.getFullYear();
        const mm = (d: Date) => String(d.getMonth() + 1).padStart(2, "0");
        const dd = (d: Date) => String(d.getDate()).padStart(2, "0");
        const date_from = `${yyyy(start)}-${mm(start)}-${dd(start)}`;
        const date_to = `${yyyy(today)}-${mm(today)}-${dd(today)}`;
        const res = await api.post("/admin/users/", userPayload as any, {
          params: { app_id: appId, date_from, date_to },
        });
        const roleName =
          res?.data?.role?.name || getRoleNameFromId(newUser.selectedRoleId);
        const commission =
          (res?.data?.commission_percentage ??
            Number(newUser.commissionPercentage)) ||
          0;
        const newUserData = {
          id: Number(res?.data?.user?.id),
          name: `${newUser.firstName} ${newUser.lastName}`,
          mobileNumber: newUser.mobileNumber,
          points: res?.data?.initial_points ?? 0,
          roleId: newUser.selectedRoleId!,
          role: roleName,
          commissionPercentage: commission,
          createdAt: new Date().toISOString().slice(0, 19).replace("T", " "),
        } as any;
        setCreatedUsers((prev) => {
          const updated = [newUserData, ...prev];
          localStorage.setItem("createdUsers", JSON.stringify(updated));
          return updated;
        });
        handleCloseUserForm();
      } else {
        await dispatch(createUser(userPayload)).unwrap();
        handleCloseUserForm();
      }
    } catch (error: any) {
      console.error("Failed to create user:", error);
      // Extract error message from backend response
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create user";

      // Only set error in mobile number field if error is related to mobile number
      if (
        errorMessage.toLowerCase().includes("mobile number") ||
        errorMessage.toLowerCase().includes("mobile") ||
        errorMessage.toLowerCase().includes("phone")
      ) {
        setUserErrors((prev) => ({
          ...prev,
          mobileNumber: errorMessage,
        }));
      }
      // Don't set errors in other fields - just log the error
    }
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setNewUser({
      mobileNumber: user.mobileNumber,
      password: "",
      firstName: user.name.split(" ")[0],
      lastName: user.name.split(" ").slice(1).join(" "),
      selectedRoleId: user.roleId || null,
      commissionPercentage: "",
    });
    setShowEditUserForm(true);
  };

  const handleDeleteUser = (user: any) => {
    setDeletingUser(user);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser || !deletingUser.id) {
      return;
    }

    setDeleteUserLoading(true);
    setDeleteUserError(null);
    setDeleteUserSuccess(null);

    try {
      const payload = {
        operation: "delete",
        user_id: deletingUser.id,
      };

      const response = await api.post("/admin/users/", payload);

      if (response.data && response.data.success) {
        // Show success message
        setDeleteUserSuccess(
          response.data.message || "User deleted successfully",
        );

        // Remove user from createdUsers if it exists there
        setCreatedUsers((prev) => {
          const updated = prev.filter((u) => u.id !== deletingUser.id);
          localStorage.setItem("createdUsers", JSON.stringify(updated));
          return updated;
        });

        // Refresh the roles with users to get updated data from API
        dispatch(fetchRolesWithUsers());

        // Close the delete modal after a short delay
        setTimeout(() => {
          setDeletingUser(null);
          setDeleteUserSuccess(null);
        }, 1500);
      } else {
        throw new Error(response.data?.message || "Failed to delete user");
      }
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete user";
      setDeleteUserError(errorMessage);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeletingUser(null);
    setDeleteUserError(null);
    setDeleteUserSuccess(null);
  };

  const handleAgentRowClick = (user: any, e: React.MouseEvent) => {
    // Only navigate if clicking on the row itself, not on buttons
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("svg") ||
      target.closest("path")
    ) {
      return;
    }

    // Check if user is an Agent (roleId 7 or role name contains "Agent")
    const isAgent = Boolean(
      user.role && user.role.toLowerCase().includes("agent"),
    );
    if (isAgent && user.id) {
      navigate(`/agent-commission-summary/${user.id}`);
    }
  };

  const handleEditUserSubmit = () => {
    const mobileError = validateMobileNumber(newUser.mobileNumber);
    const passwordError = newUser.password
      ? validatePassword(newUser.password)
      : "";
    const firstNameError = validateName(newUser.firstName, "First name");
    const lastNameError = validateName(newUser.lastName, "Last name");
    const roleSelectionError = !newUser.selectedRoleId
      ? "Please select a role"
      : "";

    setUserErrors({
      mobileNumber: mobileError,
      password: passwordError,
      firstName: firstNameError,
      lastName: lastNameError,
      roleSelection: roleSelectionError,
      commissionPercentage: "",
    });

    if (
      mobileError ||
      passwordError ||
      firstNameError ||
      lastNameError ||
      roleSelectionError
    ) {
      return;
    }

    const roleName = getRoleNameFromId(newUser.selectedRoleId);

    setCreatedUsers((prev) => {
      const updated = prev.map((user) =>
        user.id === editingUser.id
          ? {
              ...user,
              name: `${newUser.firstName} ${newUser.lastName}`,
              mobileNumber: newUser.mobileNumber,
              roleId: newUser.selectedRoleId!,
              role: roleName,
            }
          : user,
      );
      localStorage.setItem("createdUsers", JSON.stringify(updated));
      return updated;
    });

    setShowEditUserForm(false);
    setEditingUser(null);
    handleCloseUserForm();
  };

  const handleToggleStatus = (role: Role) => {
    // Implement toggle status functionality
    setRoles((prev) =>
      prev.map((r) => (r.id === role.id ? { ...r, isActive: !r.isActive } : r)),
    );
  };

  const handleEditPermissions = (role: Role) => {
    // Implement edit permissions functionality
    console.log("Edit permissions for role:", role);
  };

  // Extract all users from rolesWithUsers and format them for the table
  const allUsersFromRoles = useMemo(() => {
    if (!rolesWithUsers || !Array.isArray(rolesWithUsers)) return [];

    const users: any[] = [];
    rolesWithUsers.forEach((role: any) => {
      if (Array.isArray(role.users)) {
        role.users.forEach((user: any) => {
          users.push({
            id: user.id,
            name: user.name || "",
            mobileNumber: user.mobile_number || "",
            role: role.name || "",
            roleId: role.id,
            commissionPercentage: user.commission_percentage ?? "-",
            isActive: user.is_active,
            createdAt: user.created_at
              ? new Date(user.created_at)
                  .toISOString()
                  .slice(0, 19)
                  .replace("T", " ")
              : "-",
          });
        });
      }
    });
    return users;
  }, [rolesWithUsers]);

  // Combine createdUsers and allUsersFromRoles, removing duplicates by mobile number and filtering out "user" role
  const allUsers = useMemo(() => {
    const combined = [...allUsersFromRoles];
    const existingMobileNumbers = new Set(
      allUsersFromRoles.map((u) => u.mobileNumber),
    );

    // Add createdUsers that don't already exist in allUsersFromRoles
    createdUsers.forEach((user) => {
      if (!existingMobileNumbers.has(user.mobileNumber)) {
        combined.push(user);
      }
    });

    // Filter out users with role "user" (case-insensitive)
    return combined.filter((user) => {
      const roleName = (user.role || "").toLowerCase();
      return roleName !== "user";
    });
  }, [allUsersFromRoles, createdUsers]);

  // Show loading state while fetching roles
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading roles...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if roles failed to load
  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">Error: {error}</div>
            <button
              onClick={() => dispatch(fetchRoles())}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            User & Role Management
          </h1>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreateUser}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Shield className="h-4 w-4" />
              Roles
            </button>
          </div>
        </div>
      </div>

      {/* Success Messages */}
      {lastCreatedRole && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Role Created Successfully!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  {lastCreatedRole.message ||
                    "Role has been created successfully"}
                </p>
                <p>Role Name: {lastCreatedRole.name}</p>
                <p className="mt-1 text-xs">
                  You can now create users with this role.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {lastCreatedUser && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <UserCheck className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                User Created Successfully!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{lastCreatedUser.message}</p>
                <p>
                  User: {lastCreatedUser.user.first_name}{" "}
                  {lastCreatedUser.user.last_name}
                </p>
                <p>
                  Role: {formatRoleNameForDisplay(lastCreatedUser.role.name)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {createRoleError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <UserX className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Creating Role
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{createRoleError}</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => dispatch(clearCreateRoleError())}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {createUserError && !showCreateUserForm && !showEditUserForm && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <UserX className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Creating User
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{createUserError}</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => dispatch(clearCreateUserError())}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Success Message */}
      {deleteUserSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <UserCheck className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                User Deleted Successfully!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{deleteUserSuccess}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Error Message */}
      {deleteUserError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <UserX className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Deleting User
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{deleteUserError}</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setDeleteUserError(null)}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pt-16">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6 pb-10 max-h-[85vh] overflow-visible mt-6">
            <h3 className="text-lg font-semibold mb-4">Create New User</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.mobileNumber ? "border-red-500" : "border-gray-300"
                }`}
                name="mobileNumber"
                value={newUser.mobileNumber}
                onChange={handleUserInputChange}
                placeholder="Enter 10-digit mobile number"
                maxLength={10}
              />
              {userErrors.mobileNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.mobileNumber}
                </p>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.password ? "border-red-500" : "border-gray-300"
                }`}
                name="password"
                value={newUser.password}
                onChange={handleUserInputChange}
                placeholder="Enter password (max 12 characters)"
                maxLength={12}
              />
              {userErrors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.password}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    userErrors.firstName ? "border-red-500" : "border-gray-300"
                  }`}
                  name="firstName"
                  value={newUser.firstName}
                  onChange={handleUserInputChange}
                  placeholder="First name"
                  maxLength={30}
                />
                {userErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {userErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    userErrors.lastName ? "border-red-500" : "border-gray-300"
                  }`}
                  name="lastName"
                  value={newUser.lastName}
                  onChange={handleUserInputChange}
                  placeholder="Last name"
                  maxLength={30}
                />
                {userErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {userErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Role
              </label>
              <select
                name="selectedRoleId"
                value={newUser.selectedRoleId || ""}
                onChange={handleUserInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.roleSelection
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select a role</option>
                {selectableRoles.map((role: any) => (
                  <option key={role.id} value={role.id}>
                    {formatRoleNameForDisplay(role.name)}
                  </option>
                ))}
              </select>
              {userErrors.roleSelection && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.roleSelection}
                </p>
              )}
              {isAgentRoleId(newUser.selectedRoleId) && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Percentage
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      userErrors.commissionPercentage
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    name="commissionPercentage"
                    value={newUser.commissionPercentage}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNewUser((prev) => ({
                        ...prev,
                        commissionPercentage: v,
                      }));
                      setUserErrors((prev) => ({
                        ...prev,
                        commissionPercentage: isAgentRoleId(
                          newUser.selectedRoleId,
                        )
                          ? validateCommission(v)
                          : "",
                      }));
                    }}
                    placeholder="e.g. 5 or 7.5"
                  />
                  {userErrors.commissionPercentage && (
                    <p className="mt-1 text-sm text-red-600">
                      {userErrors.commissionPercentage}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                onClick={handleCloseUserForm}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSubmitUser}
                disabled={createUserLoading || hasUserValidationErrors()}
              >
                {createUserLoading ? "Creating..." : "Create Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserForm && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 pt-16">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 mt-5">
            <h3 className="text-lg font-semibold mb-4">Edit User</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.mobileNumber ? "border-red-500" : "border-gray-300"
                }`}
                name="mobileNumber"
                value={newUser.mobileNumber}
                onChange={handleUserInputChange}
                placeholder="Enter 10-digit mobile number"
                maxLength={10}
              />
              {userErrors.mobileNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.mobileNumber}
                </p>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password (leave blank to keep current)
              </label>
              <input
                type="password"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.password ? "border-red-500" : "border-gray-300"
                }`}
                name="password"
                value={newUser.password}
                onChange={handleUserInputChange}
                placeholder="Enter new password"
                maxLength={12}
              />
              {userErrors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.password}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    userErrors.firstName ? "border-red-500" : "border-gray-300"
                  }`}
                  name="firstName"
                  value={newUser.firstName}
                  onChange={handleUserInputChange}
                  placeholder="First name"
                  maxLength={30}
                />
                {userErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {userErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    userErrors.lastName ? "border-red-500" : "border-gray-300"
                  }`}
                  name="lastName"
                  value={newUser.lastName}
                  onChange={handleUserInputChange}
                  placeholder="Last name"
                  maxLength={30}
                />
                {userErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {userErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Role
              </label>
              <select
                name="selectedRoleId"
                value={newUser.selectedRoleId || ""}
                onChange={handleUserInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  userErrors.roleSelection
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select a role</option>
                {selectableRoles.map((role: any) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {userErrors.roleSelection && (
                <p className="mt-1 text-sm text-red-600">
                  {userErrors.roleSelection}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                onClick={handleCloseUserForm}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleEditUserSubmit}
              >
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Card */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 pt-16">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 mt-5">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete User
                </h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete user{" "}
                <span className="font-semibold">"{deletingUser.name}"</span>?
              </p>
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600">
                  <p>
                    <strong>Name:</strong> {deletingUser.name}
                  </p>
                  <p>
                    <strong>Phone:</strong> {deletingUser.mobileNumber}
                  </p>
                  <p>
                    <strong>Role:</strong> {deletingUser.role}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={cancelDeleteUser}
                disabled={deleteUserLoading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={confirmDeleteUser}
                disabled={deleteUserLoading}
              >
                {deleteUserLoading ? "Deleting..." : "Delete User"}
              </button>
            </div>
            {deleteUserError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{deleteUserError}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Show all users from roles and created users */}
              {allUsers.map((user, index) => (
                <tr
                  key={`user-${user.id}-${user.mobileNumber}`}
                  className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${user.role && user.role.toLowerCase().includes("agent") ? "cursor-pointer hover:bg-orange-50 transition-colors" : ""}`}
                  onClick={(e) => handleAgentRowClick(user, e)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-gray-400 mr-2" />
                      {user.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.mobileNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-blue-800">
                      {formatRoleNameForDisplay(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.commissionPercentage ?? "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      <UserCheck className="w-3 h-3 mr-1" />
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-800 p-1 rounded"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.createdAt}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {allUsers.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No users found.</p>
        </div>
      )}
    </div>
  );
};

export default RoleCreation;
